import { useEffect, useRef, useState } from "react";
import { api, API, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HardDrive, Upload, Download, Trash2, Send } from "lucide-react";

const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function Files() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/files").then((r) => setFiles(r.data));
  useEffect(() => {
    load();
  }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Max file size is 20 MB.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/files", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const download = (f) => {
    const token = localStorage.getItem(TOKEN_KEY);
    window.open(`${API}/files/${f.id}/download?auth=${token}`, "_blank");
  };

  const remove = async (f) => {
    try {
      await api.delete(`/files/${f.id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <HardDrive size={22} />
          <h1 className="text-2xl font-black tracking-tight">Files</h1>
        </div>
        <Button onClick={() => inputRef.current?.click()} disabled={busy}
          className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="file-upload-button">
          <Upload size={16} className="mr-2" /> {busy ? "Uploading…" : "Upload"}
        </Button>
        <input ref={inputRef} type="file" className="hidden" onChange={onPick} data-testid="file-input" />
      </div>
      <p className="text-sm text-slate-600 mb-6">
        Stored on your workspace's Telegram bot when connected (free, ~unlimited) — otherwise in the database. Max 20 MB per file.
      </p>

      <Card className="rounded-sm border-border shadow-none divide-y divide-border max-w-4xl">
        {files.length === 0 && <div className="p-6 text-sm text-slate-500">No files yet. Upload one to get started.</div>}
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 p-3" data-testid={`file-row-${f.id}`}>
            <div className="w-9 h-9 bg-muted rounded-sm flex items-center justify-center">
              <HardDrive size={16} className="text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{f.name}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                {fmtSize(f.size)}
                {f.backend === "telegram" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-sky-600">
                    <Send size={10} /> Telegram
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => download(f)} className="text-slate-500 hover:text-slate-950" data-testid={`file-download-${f.id}`}>
              <Download size={16} />
            </button>
            <button onClick={() => remove(f)} className="text-slate-400 hover:text-destructive" data-testid={`file-delete-${f.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </Card>
    </div>
  );
}
